import { useEffect, useState, useCallback } from 'react';
import { listingsApi } from '@/services/api';
import { actionBadge, formatAuditDate, type AuditRow } from './auditFormat';

interface PropertyOption {
  id: number;
  title: string;
}

export default function PropertyAuditModal({
  open,
  onClose,
  properties,
  initialPropertyId,
}: {
  open: boolean;
  onClose: () => void;
  properties: PropertyOption[];
  initialPropertyId: number | null;
}) {
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterId, setFilterId] = useState<number | null>(initialPropertyId);

  const fetchAudit = useCallback(async (propertyId: number | null) => {
    setLoading(true);
    setError('');
    try {
      const res = await listingsApi.getAudit(propertyId ?? undefined);
      setRows((res.data ?? []) as AuditRow[]);
    } catch (err) {
      setRows([]);
      setError(err instanceof Error ? err.message : 'Failed to load audit log.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setFilterId(initialPropertyId);
  }, [initialPropertyId]);

  useEffect(() => {
    if (open) {
      setFilterId(initialPropertyId);
      fetchAudit(initialPropertyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialPropertyId]);

  if (!open) return null;

  const changeFilter = (id: number | null) => {
    setFilterId(id);
    fetchAudit(id);
  };

  const selectedTitle = filterId ? properties.find((p) => p.id === filterId)?.title : null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200/60 bg-white">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950">Property Audit Log</h2>
            <p className="text-xs text-foreground-500">Admin-only trail of who changed what, and when</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-200/60 text-foreground-500 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        {/* Filter bar */}
        <div className="px-6 py-3 border-b border-background-200/60 bg-background-50/60 flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="relative flex-1 max-w-xs">
            <i className="ri-filter-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm" />
            <select
              value={filterId ?? ''}
              onChange={(e) => changeFilter(e.target.value ? Number(e.target.value) : null)}
              className="w-full pl-9 pr-4 py-2 rounded-lg bg-white border border-background-200/60 text-sm text-foreground-950 focus:outline-none focus:border-accent-500/50 cursor-pointer"
            >
              <option value="">All properties</option>
              {properties.map((p) => (
                <option key={p.id} value={p.id}>{p.id} — {p.title}</option>
              ))}
            </select>
          </div>
          {filterId && (
            <button
              onClick={() => changeFilter(null)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-accent-100 text-accent-700 border border-accent-200 hover:bg-accent-200/70 transition-colors cursor-pointer whitespace-nowrap"
              title="Clear filter"
            >
              <i className="ri-close-line" />
              {selectedTitle || `Property #${filterId}`}
            </button>
          )}
          <span className="text-xs text-foreground-500 sm:ml-auto whitespace-nowrap">{rows.length} record(s)</span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-12 space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-14 bg-background-200/60 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : error ? (
            <div className="p-12 text-center">
              <i className="ri-alert-line text-2xl text-rose-400 mb-2 block" />
              <p className="text-sm text-foreground-500">{error}</p>
            </div>
          ) : rows.length === 0 ? (
            <div className="p-12 text-center">
              <i className="ri-file-list-3-line text-3xl text-foreground-300 mb-2 block" />
              <p className="text-sm text-foreground-500">No audit entries found{filterId ? ' for the selected property' : ''}.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-background-50/95 backdrop-blur">
                  <tr className="border-b border-background-200/60">
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Action</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Property</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">By</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">Details</th>
                    <th className="text-left text-[10px] font-semibold text-foreground-500 uppercase tracking-wider px-5 py-3">When</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const badge = actionBadge(r.action);
                    return (
                      <tr key={r.id} className="border-b border-background-200/50 hover:bg-background-200/40 transition-colors">
                        <td className="px-5 py-3">
                          <span className={`inline-flex items-center gap-1.5 text-[10px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap ${badge.cls}`}>
                            <i className={badge.icon} /> {badge.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 max-w-[220px]">
                          <p className="text-xs font-medium text-foreground-950 truncate">{r.property_title || `Property #${r.property_id ?? '?'}`}</p>
                          {r.property_id && <p className="text-[10px] text-foreground-400">ID #{r.property_id}</p>}
                        </td>
                        <td className="px-5 py-3 text-xs text-foreground-600 whitespace-nowrap">{r.by_name || 'System'}</td>
                        <td className="px-5 py-3 text-xs text-foreground-500 max-w-[260px] truncate" title={r.details || ''}>{r.details || '—'}</td>
                        <td className="px-5 py-3 text-xs text-foreground-500 whitespace-nowrap">{formatAuditDate(r.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}